import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { IconExternalLink, IconPackage, IconPlug } from "@tabler/icons-react";

import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { FactRow } from "@/components/dashboard/fact-row";
import { IconButton } from "@/components/dashboard/icon-button";
import {
  describeChange,
  MetricPanel,
  MetricStripSkeleton,
  type Metric,
} from "@/components/dashboard/metric-cell";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import { productService } from "@/services/product.service";
import type {
  ProductRow,
  RecurringAnalytics,
  RecurringProductRow,
  RenewalRow,
  RenewingSubscriptionRow,
} from "@/types/products-analytics";
import { formatAmount, formatNumber } from "@/utils/formatters";

const shortDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function ProductCell({ title, image }: { title: string; image: string | null }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground">
        {image ? (
          <img src={image} alt="" className="h-full w-full object-cover" />
        ) : (
          <IconPackage size={14} stroke={1.75} />
        )}
      </span>
      <span className="block max-w-[16rem] truncate font-medium text-foreground" title={title}>
        {title}
      </span>
    </span>
  );
}

const money = (value: number) => (
  <span className="tabular-nums font-medium text-foreground">{formatAmount(value)}</span>
);
const count = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

const topProductColumns = (withRecurring: boolean): ColumnDef<ProductRow>[] => [
  {
    accessorKey: "title",
    header: "Product",
    cell: ({ row }) => <ProductCell title={row.original.title} image={row.original.image} />,
  },
  { accessorKey: "units", header: "Units", meta: { fit: true }, cell: ({ row }) => count(row.original.units) },
  { accessorKey: "orders", header: "Orders", meta: { fit: true }, cell: ({ row }) => count(row.original.orders) },
  {
    accessorKey: "affiliates",
    header: "Affiliates",
    meta: { fit: true },
    cell: ({ row }) => count(row.original.affiliates),
  },
  ...(withRecurring
    ? [
        {
          accessorKey: "recurringShare",
          header: "Recurring",
          meta: { fit: true },
          cell: ({ row }) => (
            <span className="tabular-nums text-muted-foreground">
              {row.original.recurringShare > 0
                ? `${Math.round(row.original.recurringShare * 100)}%`
                : "One-time only"}
            </span>
          ),
        } satisfies ColumnDef<ProductRow>,
      ]
    : []),
  { accessorKey: "revenue", header: "Revenue", meta: { fit: true }, cell: ({ row }) => money(row.original.revenue) },
];

const recurringProductColumns: ColumnDef<RecurringProductRow>[] = [
  {
    accessorKey: "title",
    header: "Product",
    cell: ({ row }) => <ProductCell title={row.original.title} image={row.original.image} />,
  },
  { accessorKey: "renewals", header: "Renewals", meta: { fit: true }, cell: ({ row }) => count(row.original.renewals) },
  {
    accessorKey: "subscriptions",
    header: "Subscriptions",
    meta: { fit: true },
    cell: ({ row }) => count(row.original.subscriptions),
  },
  {
    accessorKey: "commission",
    header: "Commission",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original.commission)}</span>,
  },
  { accessorKey: "revenue", header: "Revenue", meta: { fit: true }, cell: ({ row }) => money(row.original.revenue) },
];

const renewalColumns: ColumnDef<RenewalRow>[] = [
  {
    accessorKey: "orderNumber",
    header: "Order",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="font-medium tabular-nums text-foreground">#{row.original.orderNumber}</span>
    ),
  },
  {
    accessorKey: "affiliateName",
    header: "Affiliate",
    cell: ({ row }) => (
      <span className="block max-w-[12rem] truncate text-foreground" title={row.original.affiliateName}>
        {row.original.affiliateName}
      </span>
    ),
  },
  {
    accessorKey: "productTitle",
    header: "Product",
    cell: ({ row }) => (
      <span className="block max-w-[14rem] truncate text-muted-foreground" title={row.original.productTitle ?? undefined}>
        {row.original.productTitle ?? "Product"}
        {row.original.items > 1 ? ` and ${formatNumber(row.original.items - 1)} more` : ""}
      </span>
    ),
  },
  { accessorKey: "amount", header: "Amount", meta: { fit: true }, cell: ({ row }) => money(row.original.amount) },
  {
    accessorKey: "commission",
    header: "Commission",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original.commission)}</span>,
  },
  {
    accessorKey: "createdAt",
    header: "Date",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">{shortDate(row.original.createdAt)}</span>
    ),
  },
  {
    id: "open",
    header: "",
    meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
    cell: ({ row }) =>
      row.original.orderStatusUrl ? (
        <IconButton
          label="Open order in Shopify"
          icon={<IconExternalLink stroke={1.75} />}
          onClick={() => window.open(row.original.orderStatusUrl!, "_blank", "noopener,noreferrer")}
        />
      ) : null,
  },
];

const renewingColumns: ColumnDef<RenewingSubscriptionRow>[] = [
  {
    accessorKey: "affiliateName",
    header: "Affiliate",
    cell: ({ row }) => (
      <span className="block max-w-[12rem] truncate font-medium text-foreground" title={row.original.affiliateName}>
        {row.original.affiliateName}
      </span>
    ),
  },
  {
    accessorKey: "rechargeSubscriptionId",
    header: "Subscription",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.rechargeSubscriptionId}</span>
    ),
  },
  { accessorKey: "renewals", header: "Renewals", meta: { fit: true }, cell: ({ row }) => count(row.original.renewals) },
  {
    accessorKey: "renewalsPaid",
    header: "Paid",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums text-muted-foreground">
        {row.original.paidCap === null
          ? `${formatNumber(row.original.renewalsPaid)}, no cap`
          : `${formatNumber(row.original.renewalsPaid)} of ${formatNumber(row.original.paidCap)}`}
      </span>
    ),
  },
  { accessorKey: "revenue", header: "Revenue", meta: { fit: true }, cell: ({ row }) => money(row.original.revenue) },
  {
    accessorKey: "lastRenewalAt",
    header: "Last renewal",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {row.original.lastRenewalAt ? shortDate(row.original.lastRenewalAt) : "None yet"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { fit: true },
    cell: ({ row }) => <StatusText status={row.original.status} />,
  },
];

function AnalyticsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
          <div className="h-4 w-16 rounded bg-muted" />
          <div className="h-6 w-40 rounded bg-muted" />
        </div>
        <MetricStripSkeleton count={4} columns="lg:grid-cols-4" />
      </div>
      <DashboardCard title="Top products" bodyClassName="px-3 pb-3">
        <DataTable columns={topProductColumns(false)} data={[]} isLoading skeletonRows={6} />
      </DashboardCard>
    </div>
  );
}

function SubscriptionsBlocks({
  recurring,
  days,
  isLoading,
}: {
  recurring: RecurringAnalytics;
  days: number;
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const { split, subscriptions } = recurring;
  const metrics: Metric[] = [
    {
      id: "products-recurring-revenue",
      label: "Recurring revenue",
      value: formatAmount(split.recurring.revenue),
      change: describeChange(split.recurring.revenue, split.previous.recurring.revenue, days, formatAmount),
      series: recurring.revenueByDay.recurring,
      unit: "revenue",
      format: formatAmount,
    },
    {
      id: "products-one-time-revenue",
      label: "One-time revenue",
      value: formatAmount(split.oneTime.revenue),
      change: describeChange(split.oneTime.revenue, split.previous.oneTime.revenue, days, formatAmount),
      series: recurring.revenueByDay.oneTime,
      unit: "revenue",
      format: formatAmount,
    },
    {
      id: "products-renewals",
      label: "Renewals",
      value: formatNumber(split.recurring.orders),
      change: describeChange(split.recurring.orders, split.previous.recurring.orders, days, formatNumber),
    },
    {
      id: "products-active-subscriptions",
      label: "Active subscriptions",
      value: formatNumber(subscriptions.active),
      change:
        subscriptions.active + subscriptions.cancelled > 0
          ? `${formatNumber(subscriptions.retentionRate)}% retained all time`
          : "No attributed subscriptions yet",
    },
  ];

  const topReasons = subscriptions.reasons.slice(0, 5);

  return (
    <>
      <MetricPanel id="products-subscriptions-panel" title="Subscriptions" metrics={metrics} columns="lg:grid-cols-4" loading={isLoading}>
        {!recurring.connected && (
          <p className="px-5 pb-4 text-xs text-muted-foreground">
            Recharge is disconnected. Figures stop at the disconnect.{" "}
            <Link to="/brand/integrations/recharge" className="font-medium text-foreground hover:underline">
              Reconnect
            </Link>
          </p>
        )}
      </MetricPanel>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8">
          <DashboardCard title="Top recurring products" bodyClassName="px-3 pb-3">
            <DataTable
              columns={recurringProductColumns}
              data={recurring.topProducts}
              isLoading={isLoading}
              skeletonRows={5}
              getRowId={(row) => row.productId}
              onRowClick={(row) => navigate(`/brand/products/insights/${encodeURIComponent(row.productId)}`)}
              emptyText="No renewals in this period"
            />
          </DashboardCard>
        </div>
        <div className="min-w-0 lg:col-span-4">
          <DashboardCard title="Cancellations" bodyClassName="px-5 pb-2">
            <FactRow label="Cancelled" value={formatNumber(subscriptions.cancelledInPeriod)} note={`last ${days} days`} />
            <FactRow label="Started" value={formatNumber(subscriptions.activatedInPeriod)} note={`last ${days} days`} />
            <FactRow label="Retention" value={`${formatNumber(subscriptions.retentionRate)}%`} note="all time" />
            {topReasons.length > 0 ? (
              topReasons.map((r) => (
                <FactRow key={r.reason} label={r.reason} value={formatNumber(r.count)} />
              ))
            ) : (
              <p className="border-t border-border py-3 text-xs text-muted-foreground">
                No cancellations in this period
              </p>
            )}
          </DashboardCard>
        </div>
      </div>

      <DashboardCard title="Latest renewals" bodyClassName="px-3 pb-3">
        <DataTable
          columns={renewalColumns}
          minTableWidth="min-w-[48rem]"
          data={recurring.latestRenewals}
          isLoading={isLoading}
          skeletonRows={5}
          getRowId={(row) => row.saleId}
          emptyText="No renewals in this period"
        />
      </DashboardCard>

      <DashboardCard title="Most renewed subscriptions" bodyClassName="px-3 pb-3">
        <DataTable
          columns={renewingColumns}
          minTableWidth="min-w-[44rem]"
          data={recurring.topRenewing}
          isLoading={isLoading}
          skeletonRows={5}
          getRowId={(row) => row.subscriptionId}
          onRowClick={(row) => navigate(`/brand/affiliates/${row.affiliateId}`)}
          emptyText="No attributed subscriptions yet"
        />
      </DashboardCard>
    </>
  );
}

/**
 * Products > Analytics. The period strip and top products are for every
 * brand; the Subscriptions blocks (one-time vs recurring, retention, churn,
 * renewals) appear once the brand has connected Recharge.
 */
export function ProductsAnalytics() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ["products-analytics", days],
    queryFn: () => productService.getAnalytics(days),
    placeholderData: keepPreviousData,
  });
  const data = query.data;
  const refreshing = query.isFetching && !!data;

  const activityMetrics = useMemo<Metric[]>(() => {
    if (!data) return [];
    const a = data.activity;
    return [
      {
        id: "products-revenue",
        label: "Revenue",
        value: formatAmount(a.revenue.value),
        change: describeChange(a.revenue.value, a.revenue.previous, days, formatAmount),
        series: a.revenue.byDay,
        unit: "revenue",
        format: formatAmount,
      },
      {
        id: "products-orders",
        label: "Orders",
        value: formatNumber(a.orders.value),
        change: describeChange(a.orders.value, a.orders.previous, days, formatNumber),
        series: a.orders.byDay,
        unit: "order",
      },
      {
        id: "products-units",
        label: "Units",
        value: formatNumber(a.units.value),
        change: describeChange(a.units.value, a.units.previous, days, formatNumber),
        series: a.units.byDay,
        unit: "unit",
      },
      {
        id: "products-sold",
        label: "Products sold",
        value: formatNumber(a.productsSold.value),
        change: describeChange(a.productsSold.value, a.productsSold.previous, days, formatNumber),
      },
    ];
  }, [data, days]);

  if (query.isLoading || !data) {
    if (query.isError) {
      return (
        <div className="space-y-4">
          <PageHeader title="Product analytics" />
          <EmptyState
            title="Could not load product analytics"
            body="Check your connection and try again"
            action={
              <Button variant="outline" onClick={() => query.refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <PageHeader title="Product analytics" />
        <AnalyticsSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Product analytics" />

      <MetricPanel
        id="products-activity-panel"
        title="Activity"
        metrics={activityMetrics}
        columns="lg:grid-cols-4"
        loading={refreshing}
        action={<PeriodToggle value={days} onChange={setDays} disabled={refreshing} />}
      />

      <DashboardCard title="Top products" to="/brand/products/list" bodyClassName="px-3 pb-3">
        <DataTable
          columns={topProductColumns(data.recurring !== null)}
          data={data.topProducts}
          isLoading={refreshing}
          skeletonRows={5}
          getRowId={(row) => row.productId}
          onRowClick={(row) => navigate(`/brand/products/insights/${encodeURIComponent(row.productId)}`)}
          emptyText="No product sales in this period"
        />
      </DashboardCard>

      {data.recurring ? (
        <SubscriptionsBlocks recurring={data.recurring} days={days} isLoading={refreshing} />
      ) : (
        <DashboardCard title="Subscriptions">
          <EmptyState
            bordered={false}
            icon={<IconPlug stroke={1.75} />}
            title="Connect Recharge to see recurring sales"
            body="One-time and renewal sales, retention and cancellations by product"
            action={
              <Button asChild variant="outline">
                <Link to="/brand/integrations/recharge">Connect Recharge</Link>
              </Button>
            }
          />
        </DashboardCard>
      )}
    </div>
  );
}

export default ProductsAnalytics;
